import {ContentBlocks_DoubleTile as DoubleTileType} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/content_blocks.types';
import {
  ContentBlockCTA,
  ContentBlockMedia,
  RowSection,
  tabletAndUp,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React, {FC} from 'react';
import sc, {css} from 'styled-components';
import {useContentBlockPromotionTracking} from '../helpers/useContentBlockPromotionTracking';
import {useContentBlockPromotionTrackingImpression} from '../helpers/useContentBlockPromotionTrackingImpression';
import {useTrackVideoPromotion} from '../helpers/useTrackVideoPromotion';
import {styleMap, transformImageVideo} from '../transforms';

export const DoubleTile: React.FC<{
  content: DoubleTileType;
  blockId?: number;
  testId?: string;
}> = ({content, blockId, testId = 'customPageDoubleTile'}) => {
  const columns = [content.first, content.second];

  const imageTileCTALink = columns
    .reduce((acc: Array<{url: string; external: boolean}>, curr) => {
      if (curr.cta) {
        return [...acc, curr.cta.link];
      }

      return acc;
    }, [])
    .find(link => link.url !== '');

  const placementImpressionpromotionName = columns
    .reduce((acc: Array<string>, curr) => {
      if (curr.cta) {
        return [...acc, curr.cta.text.text];
      }

      return acc;
    }, [])
    .find(text => text !== '');

  const {elementRef: placementRef} =
    useContentBlockPromotionTrackingImpression<HTMLDivElement>({
      blockId,
      promotionName: placementImpressionpromotionName,
      promotionId: blockId ? String(blockId) : '',
      locationBlockType: 'doubleTile',
      path: imageTileCTALink || {external: false, url: ''},
      contentBlockSource: 'banner',
    });

  return (
    <BlockWrapper
      $backgroundColor={content.backgroundColor}
      centerContent
      data-content-block={blockId}
      ref={placementRef}
    >
      <ContentWrapper data-testid={testId}>
        {columns.map((column, index) => (
          <Column
            key={index}
            column={column}
            testId={testId}
            columnIndex={index}
            blockId={blockId}
          />
        ))}
      </ContentWrapper>
    </BlockWrapper>
  );
};

const Column: FC<{
  column: DoubleTileType['first'] | DoubleTileType['second'];
  testId: string;
  columnIndex: number;
  blockId?: number;
}> = ({column, testId, blockId, columnIndex}) => {
  const promotionTrackingClick = useContentBlockPromotionTracking();

  // using the fallback id for the promotionId since the double tile
  const promotionId = blockId ? String(blockId) : '';
  const promotionName = column.cta ? column.cta.text.text : '';
  const locationBlockType = 'doubleTile';

  const {elementRef: videoElementRef, trackFeatureOnVideoEnds} =
    useTrackVideoPromotion({
      blockId,
      promotionId,
      promotionName,
      locationBlockType,
      path: {
        external: true,
        url: 'video',
      },
    });

  const handleCTAClick = (
    path: {url: string; external: boolean},
    promotionName: string,
    streamPosition: number,
  ) => {
    promotionTrackingClick({
      actionType: 'click',
      contentBlockSource: 'cta',
      locationBlockType,
      promotionId: blockId ? String(blockId) : '',
      blockId,
      promotionName,
      streamPosition,
      path,
    });
  };

  return (
    <ColumnWrapper>
      <StyledContentBlockMedia
        testId={testId}
        imageOrVideo={transformImageVideo(column.imageVideo)!}
        videoRef={videoElementRef}
        onEndVideoCallback={trackFeatureOnVideoEnds}
      />
      {column.cta && (
        <CTAsWrapper>
          <ContentBlockCTA
            testId={`${testId}CTA`}
            link={{url: column.cta.link.url}}
            text={{
              text: column.cta.text.text,
              style: styleMap[column.cta.text.style],
              textColor: column.cta.text.color,
            }}
            onClick={() => {
              if (column.cta) {
                handleCTAClick(
                  column.cta.link,
                  column.cta.text.text,
                  columnIndex,
                );
              }
            }}
          />
        </CTAsWrapper>
      )}
    </ColumnWrapper>
  );
};

const BlockWrapper = linariaStyled(RowSection)<{$backgroundColor?: string}>`
 background-color: ${({$backgroundColor}) => $backgroundColor ?? ''};
`;

const ContentWrapper = sc.div`
  display: flex;
  flex-basis: 100%;
  flex-direction: column;
  gap: 1rem;
  max-width: 1440px;
  padding: 1rem;

  ${tabletAndUp(css`
    flex-direction: row;
  `)}
`;

const StyledContentBlockMedia = sc(ContentBlockMedia)``;

const ColumnWrapper = sc.div`
  align-items: center;
  display: flex;
  flex-basis: 100%;
  flex-direction: column;
  flex-shrink: 1;
  justify-content: center;
  position: relative;

  &:last-of-type ${StyledContentBlockMedia} {
    transform: scale(0.8);
  }
`;

const CTAsWrapper = sc.div`
  position: absolute;
`;
